import { Children, FC, KeyboardEvent, MouseEvent, ReactNode, isValidElement, useCallback, useMemo, useRef } from "react";
import { MenuListProps } from "react-select";

import { Button, Icon } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { Body, Paragraph } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { Checkbox } from "../../../Checkbox";
import { Textbox } from "../../../Textbox";
import { getStyleModeSafeProps } from "../../../utils/getStyleModeSafeProps";
import { CustomReactSelectProps } from "../../CustomReactSelectProps";
import { OptionItem } from "../../OptionItem";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedDropdownMenuListStyles = createComponentStyleHook<MenuListProps<OptionItem | null>>({
  legacyStyles,
  tokenStyles,
  componentTokens: "dropdown_menu_list",
});

const getVisibleOptionValues = (menuChildren: ReactNode): ReadonlyArray<string | number> => {
  const visibleValues = new Set<string | number>();

  const visit = (node: ReactNode) => {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;

      const childProps = child.props as { data?: OptionItem; children?: ReactNode };
      const optionValue = childProps.data?.value;
      if (optionValue !== undefined) visibleValues.add(optionValue);
      if (childProps.children !== undefined) visit(childProps.children);
    });
  };

  visit(menuChildren);
  return Array.from(visibleValues);
};

/**
 * The menu list component.
 */
export const MenuList: FC<MenuListProps<OptionItem | null>> = (props) => {
  const { children, selectOption, selectProps, innerProps, innerRef, focusedOption } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedDropdownMenuListStyles(getStyleModeSafeProps(props));
  const {
    searchable = false,
    searchInputProps = {},
    searchRef,
    searchValue,
    enableCheckboxOptions,
    selectedValues,
    menuIsOpen,
    showMasterCheck,
    onMenuClose,
    onSearchChange,
    onSearchBlur,
    onSelectAll,
    isItemSelected,
    selectRef,
    onRemove,
  } = selectProps as CustomReactSelectProps;

  const menuListClassName = classNames(styles.MenuList, "menu-list items");
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const visibleOptionValues = useMemo(() => getVisibleOptionValues(children), [children]);
  const selectedVisibleCount = useMemo(() => {
    if (!selectedValues) return 0;
    return visibleOptionValues.filter((value) => selectedValues.has(value)).length;
  }, [selectedValues, visibleOptionValues]);
  const itemsCount = useMemo(() => {
    if (!selectedValues) return 0;
    return Array.from(selectedValues).length;
  }, [selectedValues]);

  const mouseDownHandler = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
  }, []);

  const handleMenuCloseWithFocusTransfer = useCallback(
    (ev: KeyboardEvent) => {
      onMenuClose?.();
      selectRef?.current?.focus();
      ev.preventDefault();
      ev.stopPropagation();
    },
    [onMenuClose, selectRef],
  );

  const keyDownHandler = useCallback(
    (ev: KeyboardEvent) => {
      const { key } = ev;

      if (key === "Enter") {
        if (document.activeElement === clearButtonRef?.current) {
          // clean up search without selecting an option
          ev.stopPropagation();
          return;
        }
        ev.preventDefault();
        ev.stopPropagation();

        const option = focusedOption;
        const optionValue = option?.value;
        if (!optionValue) return;

        const isChecked = isItemSelected?.(optionValue) ?? false;
        if (selectProps.closeMenuOnSelect || selectProps.closeMenuOnSelect === undefined) handleMenuCloseWithFocusTransfer(ev);

        if (isChecked) {
          onRemove?.(optionValue);
          return;
        }
        selectOption?.(option);
        return;
      }
      if (
        key === "Escape"
        || (key === "Tab" && !searchable)
        || (key === "Tab" && searchable && !clearButtonRef?.current)
        || (key === "Tab" && searchable && searchValue && document.activeElement === clearButtonRef?.current)
      ) {
        handleMenuCloseWithFocusTransfer(ev);
        return;
      }

      if (key === "ArrowDown" || key === "ArrowUp") {
        ev.preventDefault();
        return;
      }
      ev.stopPropagation();
    },
    [
      enableCheckboxOptions,
      focusedOption,
      isItemSelected,
      menuIsOpen,
      onMenuClose,
      onRemove,
      searchRef,
      selectOption,
      searchable,
      selectRef,
      handleMenuCloseWithFocusTransfer,
      searchValue,
      selectProps.closeMenuOnSelect,
    ],
  );

  const checkboxValue = useMemo(() => {
    if (!selectedValues) return false;
    if (visibleOptionValues.length === 0) return false;
    if (selectedVisibleCount === visibleOptionValues.length) return true;
    if (selectedVisibleCount > 0) return "indeterminate";
    return false;
  }, [selectedValues, selectedVisibleCount, visibleOptionValues.length]);

  const handleSelectAll = useCallback(() => {
    onSelectAll?.(visibleOptionValues);
  }, [onSelectAll, visibleOptionValues]);

  const clearHandler = useCallback(() => {
    if (!onSearchChange || !searchRef) return;
    onSearchChange(null, null);
    searchRef.current && searchRef.current.focus();
  }, [searchRef]);

  return (
    <StyleModeBoundary mode={mode}>
      <Body size="small">
        <Stack spacing="none">
          {searchable && (
            <div className={classNames(styles.SearchBox, "searchbox", { clearable: enableCheckboxOptions })}>
              <div onKeyDown={keyDownHandler} onMouseDown={mouseDownHandler} onBlur={onSearchBlur}>
                <Stack>
                  <Textbox
                    ref={searchRef}
                    value={searchValue}
                    onChange={onSearchChange}
                    prefix={<Icon name="search" />}
                    hideInstructions
                    hideLabel
                    placeholder="Search..."
                    {...searchInputProps}
                  />
                  {enableCheckboxOptions && searchValue && !!searchValue.length && (
                    <Button
                      ref={clearButtonRef}
                      className={classNames(styles.DropdownSearchClearButton, "dropdown-search-clear-button")}
                      icon="x"
                      size="small"
                      appearance="text"
                      onClick={clearHandler}
                      aria-label="Clear Search"
                    />
                  )}
                </Stack>
              </div>
            </div>
          )}
          {enableCheckboxOptions && showMasterCheck && (
            <div className={styles.MenuListCheckbox}>
              <Shelf spacing="medium">
                <Checkbox value={checkboxValue} onChange={handleSelectAll} label="Select all" unpadded hideLabel />
                <Shelf.Spacer />
              </Shelf>
            </div>
          )}
          <div {...innerProps} ref={innerRef} className={menuListClassName} role="listbox">
            {children}
          </div>
        </Stack>
        {enableCheckboxOptions && (
          <div className={styles.MenuListFooter}>
            <Shelf spacing="none">
              <Paragraph>{itemsCount || 0} selected</Paragraph>
              <Shelf.Spacer />
            </Shelf>
          </div>
        )}
      </Body>
    </StyleModeBoundary>
  );
};
